import React, { useCallback, useRef, useState, useMemo } from "react";
import { IconButton } from "@mui/material";
import { SettingsOutlined } from "@mui/icons-material";
import { Menu, Drawer } from "./index";
import { useRowSettingsStyles } from "./css";
import { useTranslation } from "react-i18next";
import * as modules from "../utils/modules";
import { useLocalStorage, useAuthorizationError } from "../hooks";
import {
  getProductInfoByBarcode,
  getProductInfoCustom,
} from "../modules/api/productInfo";
import { getErrorMessage } from "./form/formUtils";
import { Alert, SearchDialog } from "./index";
import { useForm } from "react-final-form";
import {
  userSearchBarcode,
  userSearchObject,
  userSearchLot,
  userSearchObject2,
  userSearchLot2,
} from "../modules/auth/selectors/userSelector";
import { useSelector } from "react-redux";
import { IScannerFields } from "./form/ScannerForm";

interface IRowSettings {
  module: modules.moduleType;
  inputRef: React.RefObject<HTMLInputElement[] | undefined>;
  index: number;
  propsForQuery?: { clientDbId: number; locale: string; departmentId: string };
  onInputLock?: (field: string, lock: boolean) => void;
  scannerFormFields?: IScannerFields[];
}

const searchFieldNames: { [key: string]: string } = {
  barcode: "BARCODE",
  lot: "LOT",
  objectCode: "OBJECT",
  lot2: "LOT2",
  objectCode2: "OBJECT2",
  locationPaletteTo: "LOCATIONPALETETO",
  locationPaletteFrom: "LOCATIONPALETEFROM",
};

function RowSettings(props: IRowSettings) {
  const { module, inputRef, propsForQuery, onInputLock, index } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const focusInputAfterMenuExit = useRef(false);
  const open = Boolean(anchorEl);
  const { classes } = useRowSettingsStyles();
  const [extraInfo, setExtraInfo] = useState<{ [name: string]: any }>();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [translate] = useTranslation();
  const [isProductCustom] = useLocalStorage("productCustomProc");
  const [openDialog, setOpenDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState({
    message: "",
    error: false,
    status: 0,
  });
  const [logoutUser] = useAuthorizationError();
  const barcodeSearch = useSelector(userSearchBarcode);
  const objectSearch = useSelector(userSearchObject);
  const lotSearch = useSelector(userSearchLot);
  const object2Search = useSelector(userSearchObject2);
  const lot2Search = useSelector(userSearchLot2);

  const { change: changeFieldValue } = useForm();
  const name =
    inputRef.current && inputRef.current[index]
      ? inputRef.current[index].name
      : "";

  const fieldName: string | undefined = searchFieldNames[name];

  const search: { [key: string]: "N" | "S" | "P" } = useMemo(
    () => ({
      BARCODE: barcodeSearch,
      OBJECT: objectSearch,
      LOT: lotSearch,
      OBJECT2: object2Search,
      LOT2: lot2Search,
    }),
    [barcodeSearch, objectSearch, lotSearch, object2Search, lot2Search],
  );

  const handleOpenMenu = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    handleCloseMenu();
    setExtraInfo({});
  };

  const handleCancelAlert = () => {
    logoutUser(errorMessage.status);

    if (errorMessage.status !== 401) {
      setErrorMessage({
        message: "",
        error: false,
        status: 0,
      });

      handleCloseMenu();
    }
  };

  const handleSearching = () => {
    setOpenDialog(true);
    handleCloseMenu();
  };

  const handleMoreInfo = () => {
    const barcode = inputRef?.current ? inputRef?.current[index]?.value : "";

    const infoParams = {
      ...propsForQuery,
      barcode: barcode,
      module: module,
    };

    const getInfo = isProductCustom
      ? getProductInfoCustom
      : getProductInfoByBarcode;

    getInfo(infoParams)
      .then(({ data }) => {
        if (Array.isArray(data)) {
          if (data.length === 1 && !data[0].type && !data[0].key) {
            setExtraInfo(data[0]);
          } else {
            setExtraInfo({ schema: data });
          }
        } else if (data && typeof data === "object") {
          setExtraInfo(data);
        } else {
          setExtraInfo({});
        }
        setOpenDrawer(true);
      })
      .catch((err: any) => {
        const message = getErrorMessage(err);
        setErrorMessage({
          message: message.message,
          error: true,
          status: message.status,
        });
      });
  };

  const lockTheInputField = useCallback(() => {
    const input = inputRef.current?.[index];
    if (!input) return;

    const unlocking = input.disabled;
    focusInputAfterMenuExit.current = unlocking && !!onInputLock;
    onInputLock?.(input.name, !unlocking);
    handleCloseMenu();
  }, [inputRef, onInputLock, index]);

  const options = React.useMemo(
    () => {
      const isSearchOn: "N" | "S" | "P" = search[fieldName] || "N";
      const options = [
        {
          name:
            inputRef.current &&
            inputRef.current[index]?.disabled &&
            inputRef.current[index]?.disabled === true
              ? translate("unlock", "Atrakinti")
              : translate("lock", "Užrakinti"),
          onClick: lockTheInputField,
        },
      ];

      if (
        fieldName === "BARCODE" ||
        fieldName === "LOCATIONPALETETO" ||
        fieldName === "LOCATIONPALETEFROM"
      ) {
        options.unshift({
          name: translate("informationByBarcode", "Informacija pagal barkodą"),
          onClick: handleMoreInfo,
        });
      }

      if (isSearchOn === "P")
        options.push({
          name: translate("search"),
          onClick: handleSearching,
        });

      return options;
    }, // eslint-disable-next-line
    [inputRef, index, lockTheInputField, handleMoreInfo, fieldName, search],
  );

  const onSearchClose = () => {
    setOpenDialog(false);
  };

  const onSearchConfirm = (value: string | null) => {
    if (inputRef.current && inputRef.current[index]?.name && value)
      changeFieldValue(inputRef.current[index]?.name, value);
    setOpenDialog(false);
    handleCloseMenu();
  };

  return (
    <>
      <SearchDialog
        openDialog={openDialog}
        field={fieldName}
        onClose={onSearchClose}
        module={module}
        onConfirm={onSearchConfirm}
        onExited={() => inputRef.current?.[index]?.focus()}
        scannerFormFields={props.scannerFormFields}
      />

      <Drawer
        isOpen={openDrawer}
        onClose={handleCloseDrawer}
        data={extraInfo}
        disableRestoreFocus
        onExited={() => inputRef.current?.[index]?.focus()}
      />

      <Alert
        message={errorMessage.message}
        isOpen={errorMessage.error}
        onClose={handleCancelAlert}
        iconColor="error"
        disableRestoreFocus
        onExited={() => inputRef.current?.[index]?.focus()}
      />

      <Menu
        open={open}
        onClose={handleCloseMenu}
        anchorEl={anchorEl}
        data={options}
        onExited={() => {
          if (!focusInputAfterMenuExit.current) return;

          focusInputAfterMenuExit.current = false;
          const input = inputRef.current?.[index];
          if (input && !input.disabled) input.focus();
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      />

      <IconButton
        onClick={handleOpenMenu}
        classes={{ root: classes.iconButton }}
      >
        <SettingsOutlined />
      </IconButton>
    </>
  );
}

export default RowSettings;
